import type { SelectedObject } from '../../list-builder/types';
import { SHARED_FILTER_REGISTRY } from '../config';
import type {
	FilterRegistry as FilterRegistryType,
	FilterState,
} from '../config/types';
import { FilterRegistry } from '../registry';

/**
 * Result from detecting an active relevance filter
 */
export interface RelevanceDetectionResult {
	/** Whether a relevance-supporting filter is active with keywords */
	hasRelevanceFilter: boolean;
	/** The filter key that supports relevance (e.g., 'clientSearch', 'agentSearch') */
	activeFilterKey: string | null;
	/** The objectTable from the filter's queryMapping */
	objectTable: string | null;
	/** The current keyword value */
	keywords: string;
}

/**
 * Detects if any active filter supports relevance sorting and has keywords.
 * Used to dynamically add/remove relevance sort option based on search state.
 *
 * @param filters Current filter state from list builder
 * @param filterRegistry Optional custom filter registry (defaults to SHARED_FILTER_REGISTRY)
 * @returns Information about the active relevance filter, if any
 */
export function detectActiveRelevanceFilter(
	filters: FilterState,
	filterRegistry: FilterRegistryType = SHARED_FILTER_REGISTRY,
): RelevanceDetectionResult {
	const emptyResult: RelevanceDetectionResult = {
		hasRelevanceFilter: false,
		activeFilterKey: null,
		objectTable: null,
		keywords: '',
	};

	if (filters == null) {
		return emptyResult;
	}

	for (const [filterKey, filterConfig] of Object.entries(filterRegistry)) {
		// Skip filters that don't support relevance
		if (!filterConfig.supportsRelevance) {
			continue;
		}

		// Check if this filter is active in the current filter state
		const filterValue = filters[filterKey];

		if (
			filterValue == null ||
			!Array.isArray(filterValue) ||
			filterValue.length === 0
		) {
			continue;
		}

		// Get the keyword value from the filter
		const firstItem = filterValue[0];
		const keywords = (
			firstItem?.label ??
			firstItem?.id?.toString() ??
			''
		).trim();

		if (keywords === '') {
			continue;
		}

		// Found an active relevance filter with keywords
		const objectTable = filterConfig.queryMapping?.objectTable ?? null;

		return {
			hasRelevanceFilter: true,
			activeFilterKey: filterKey,
			objectTable,
			keywords,
		};
	}

	return emptyResult;
}

/**
 * Get all filter keys that are valid for the given objects.
 * Includes both the static SHARED_FILTER_REGISTRY and any dynamic
 * (tenant-scoped) filters registered at runtime via
 * FilterRegistry.registerDynamicFilters — e.g. custom-field filters.
 *
 * When `listKey` is provided, filters that declare a `listKeys` restriction
 * are only valid for those lists. Callers that don't know their listKey keep
 * the legacy objects-only behavior (the restriction can't be evaluated, so
 * it isn't applied).
 */
export function getValidFiltersForObjects(
	objects: SelectedObject[],
	listKey?: string,
): string[] {
	const objectTables = [...new Set(objects.map((obj) => obj.objectTable))];

	const isValid = (config: {
		queryMapping?: { objectTable: string };
		listKeys?: string[];
	}): boolean => {
		if (
			listKey != null &&
			config.listKeys != null &&
			config.listKeys.length > 0 &&
			!config.listKeys.includes(listKey)
		) {
			return false;
		}

		return (
			!config.queryMapping ||
			objectTables.length === 0 ||
			objectTables.includes(config.queryMapping.objectTable)
		);
	};

	const fromStatic = Object.entries(SHARED_FILTER_REGISTRY)
		.filter(([_, config]) => isValid(config))
		.map(([key, _]) => key);

	const fromDynamic = Object.entries(FilterRegistry.getDynamicFilters())
		.filter(([_, config]) => isValid(config))
		.map(([key, _]) => key);

	return [...fromStatic, ...fromDynamic];
}

/**
 * Validate that active filters are appropriate for the objects (and, when
 * the caller passes its listKey, for the list — see getValidFiltersForObjects).
 */
export function validateActiveFilters(
	activeFilters: string[],
	objects: SelectedObject[],
	listKey?: string,
): string[] {
	const validFilters = getValidFiltersForObjects(objects, listKey);
	return activeFilters.filter((filter) => validFilters.includes(filter));
}
